import {
  Button,
  SlideOutModal,
  SlideOutModalProps,
  SpinnerDefault,
  Toggle,
} from "../../../components";
import * as yup from 'yup';
import { useRef, useState } from "react";
import { sharedProperty } from "../../../hooks";
import { BookingModel } from "../../../data/entities/booking/BookingModel";
import { Formik } from "formik";
import { Form, Button as BSButton, Col, FloatingLabel, Row, Card} from "react-bootstrap";
import BookingEventSummary from "../components/BookingEventSummary";
import { showErrorMessage } from "../../../utils";

export interface ConfirmBookingModalProps extends SlideOutModalProps {
  onSubmit: any;
  onBack: any;
  onConflict?: () => void;
  booking: sharedProperty<BookingModel>;
}

export function ConfirmBookingModal(props: Readonly<ConfirmBookingModalProps>) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const submitRef = useRef<HTMLButtonElement>(null);
  
  const validationSchema = yup.object().shape({
    firstName: yup.string().required('Please enter your first name'),
    lastName: yup.string().required('Please enter your last name'),
    email: yup.string().email().required('Please enter a valid email address'),
    phone: yup.string().required('Please enter your phone number'),
  });

  return (
    <SlideOutModal
      {...props}
      size="lg"
      title={"Confirm Booking"}
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={props.onBack} variant="cancel" className="w-100" disabled={isSubmitting}>
                Back
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100" disabled={isSubmitting}>
                Submit
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div style={{ position: 'relative', minHeight: '100%' }}>
        {isSubmitting && (
          <SpinnerDefault
            overlay={true}
            message={"Processing booking..."}
            submessage={"Please do not close this window"}
            large={true}
          />
        )}
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={{
            firstName: props.booking.value.customer?.firstName || '',
            lastName: props.booking.value.customer?.lastName || '',
            email: props.booking.value.customer?.email || '',
            phone: props.booking.value.customer?.phone || '',
            phoneReminder: props.booking.value.sendConfirmationToPhone,
          }}
          onSubmit={async () => {
            setIsSubmitting(true);
            try {
              await props.onSubmit(props.booking.value);
            }catch(error: any) {     
              setIsSubmitting(false);
              if (props.onConflict) {
                props.onConflict();
              } else {
                const errorMessage = error?.response?.data?.message || 
                                  error?.message || 
                                  "An unexpected error occurred.";
                showErrorMessage(errorMessage);
              }
            }
          }}
        >
          {({ handleSubmit, values, errors, handleChange, touched, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <div className="mb-4">
              <h5 className="text-muted mb-3 fw-semibold" style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Contact Information
              </h5>
              <Row>
                <Col xs={24} md={12}>
                  <FloatingLabel label="First Name" className="mb-3">
                    <Form.Control
                      type="string"
                      placeholder="First Name"
                      name="firstName"
                      value={values.firstName}
                      onChange={(value: any) => {
                        handleChange(value);
                        props.booking.setter({
                          ...props.booking.value,
                          customer: {
                            ...props.booking.value.customer,
                            firstName: value.target.value,
                          }
                        })
                      }}
                      isInvalid={touched.firstName && !!errors.firstName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
                <Col xs={24} md={12}>
                  <FloatingLabel label="Last Name" className="mb-3">
                    <Form.Control
                      type="string"
                      placeholder="Last Name"
                      name="lastName"
                      value={values.lastName}
                      onChange={(value: any) => {
                        handleChange(value);
                        props.booking.setter({
                          ...props.booking.value,
                          customer: {
                            ...props.booking.value.customer,
                            lastName: value.target.value,
                          }
                        })
                      }}
                      isInvalid={touched.lastName &&
                        !!errors.lastName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>
              
              <Row>
                <Col>
                  <FloatingLabel label="Email" className="mb-3">
                    <Form.Control
                      type="string"
                      placeholder="Email"
                      name="email"
                      value={values.email}
                      onChange={(value) => {
                        handleChange(value);
                        props.booking.setter({
                          ...props.booking.value,
                          customer: {
                            ...props.booking.value.customer,
                            email: value.target.value,
                          }
                        })
                      }}
                      isInvalid={touched.email &&
                        !!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>

              <Row>
                <Col>
                  <FloatingLabel label="Phone" className="mb-3">
                    <Form.Control
                      type="string"
                      placeholder="Phone"
                      name="phone"
                      value={values.phone}
                      onChange={(value) => {
                        handleChange(value);
                        props.booking.setter({
                          ...props.booking.value,
                          customer: {
                            ...props.booking.value.customer,
                            phone: value.target.value,
                          }
                        })
                      }}
                      isInvalid={touched.phone &&
                        !!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>
              </div>
              <div className="mb-4">
                <h5 className="text-muted mb-3 fw-semibold" style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Notification Preferences
                </h5>
                  <Toggle
                    id="phoneReminderToggle"
                    className="d-none d-md-block fs-5"
                    description="Send me text reminders about this booking"
                    initialValue={values.phoneReminder}
                    onChange={(checked) => {
                      setFieldValue("phoneReminder", checked);
                      props.booking.setter({
                        ...props.booking.value,
                        sendConfirmationToPhone: checked,
                      });
                    }}
                    iconLeft={true}
                  />
                  <Toggle
                    id="phoneReminderToggleMobile"
                    className="d-md-none small"
                    description="Send me text reminders about this booking"
                    initialValue={values.phoneReminder}
                    onChange={(checked) => {
                      setFieldValue("phoneReminder", checked);
                      props.booking.setter({
                        ...props.booking.value,
                        sendConfirmationToPhone: checked,
                      });
                    }}
                    iconLeft={true}
                  />
              </div>      
              <h5 className="text-muted mb-3 fw-semibold" style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Summary
              </h5> 
              <Row>
                <Col>
                  {props.booking.value.bookingEvents.map((event) => (
                    <BookingEventSummary 
                      key={event.id} 
                      event={event} 
                      bookingDate={props.booking.value.date!} 
                      locationId = {props.booking.value.locationId}
                      />
                  ))}
                </Col>
              </Row>

              <BSButton ref={submitRef} type="submit" className="d-none"/>
            </Form>
          )}
        </Formik>
      </div>
    </SlideOutModal>
  );
}

export default ConfirmBookingModal;